import { useDesign } from "./store";
import { isSharedRoom } from "./sharedRoomsRegistry";
import {
  startSharedRoomSync,
  stopSharedRoomSync,
} from "./sharedRoomSync";
import type { Design } from "./types";
import { captureException, track } from "./telemetry";

/**
 * Single entry point for "open a room" — replaces the old pattern of
 * sprinkling `loadDesign` / `loadSharedDesign` / `startSharedRoomSync`
 * calls (plus URL rewrites) at every call site. The decision of shared
 * vs private is driven by the data model — `sharedRoomsRegistry` — not
 * by the URL.
 *
 * Outcome rules:
 *   1. If we know (registry) or are told (forceShared) this roomId is
 *      shared, try to start `startSharedRoomSync`. On success, mirror
 *      `?shared=<roomId>` into the URL for deep-linkability and return.
 *   2. Otherwise (or if shared sync fails — e.g. offline, membership
 *      revoked between the registry snapshot and now), fall back to a
 *      private load using `fallbackDesign` if provided.
 *   3. Either way, the URL is treated as a *consequence* of state: we
 *      `writeSharedUrlParam` on shared entry and `clearSharedUrlParam`
 *      on private entry — never as the trigger for state changes.
 *
 * Why a unified helper: previously `?shared=` was the only thing that
 * booted `startSharedRoomSync`, so loading a saved-library room that
 * happened to also be shared silently dropped into private mode (no
 * collaborators visible). With this helper every entry point —
 * `RoomsSheet` library load, "Shared with me" inbox click, invite
 * accept, deep link, and reload — flows through the same decision.
 */

export interface OpenRoomOptions {
  /** Snapshot to load if shared mode isn't entered. Required if the
   *  caller wants the store's `design` swapped out (e.g. library load).
   *  Omit when the caller already has the design in place and just wants
   *  the registry to decide whether to upgrade to shared mode. */
  fallbackDesign?: Design;
  /** Force the shared-room path even when the registry doesn't yet
   *  know about this id. Used for deep links (`?shared=`) and the
   *  persist-restore path where we want to honor the user's last
   *  session before the registry resolves. */
  forceShared?: boolean;
  /** Override the default access-lost handler. */
  onAccessLost?: (reason: string) => void;
}

export type OpenRoomResult = "shared" | "private" | "failed";

/**
 * Open a room and route to shared or private mode based on the data
 * model. Returns `"shared"` if multiplayer sync started, `"private"`
 * if we loaded a fallback design, or `"failed"` if neither path
 * succeeded (e.g. shared room is inaccessible AND no fallback was
 * provided).
 */
export async function openRoom(
  roomId: string,
  opts: OpenRoomOptions = {}
): Promise<OpenRoomResult> {
  const shouldTryShared = opts.forceShared || isSharedRoom(roomId);

  if (shouldTryShared) {
    try {
      await startSharedRoomSync(roomId, {
        onAccessLost: opts.onAccessLost ?? defaultAccessLostHandler,
      });
      writeSharedUrlParam(roomId);
      track("room_loaded", { id: roomId, shared: true });
      return "shared";
    } catch (err) {
      console.warn(
        "[openRoom] couldn't enter shared mode for",
        roomId,
        "— falling back to private:",
        err
      );
      captureException(err, { context: "openRoom.shared", roomId });
      // `startSharedRoomSync` calls `stopSharedRoomSync()` at its top
      // before fetching, which leaves `activeSharedRoomId` orphaned in
      // the store if the fetch then throws. Clear it so the UI doesn't
      // show a stale multiplayer banner.
      if (useDesign.getState().activeSharedRoomId) {
        useDesign.getState().clearActiveSharedRoom();
      }
    }
  }

  if (opts.fallbackDesign) {
    useDesign.getState().loadDesign(opts.fallbackDesign, roomId);
    clearSharedUrlParam();
    track("room_loaded", { id: roomId, shared: false });
    return "private";
  }

  // No shared mode and no fallback — make sure the URL doesn't pretend
  // we're in a shared room we couldn't actually enter.
  clearSharedUrlParam();
  return shouldTryShared ? "failed" : "private";
}

/**
 * Exit any active shared room and clear the deep-link URL. Used when
 * the user explicitly leaves a shared session (the URL effect noticing
 * `?shared=` was removed, or a registry-driven downgrade because
 * membership was revoked).
 */
export function exitSharedRoom(): void {
  if (useDesign.getState().activeSharedRoomId) {
    stopSharedRoomSync();
    useDesign.getState().clearActiveSharedRoom();
  }
  clearSharedUrlParam();
}

/** Mirror the active shared room into `?shared=<roomId>` via
 *  `replaceState` (no popstate) so the URL is shareable as a deep link
 *  without re-triggering URL-watching effects. */
export function writeSharedUrlParam(roomId: string): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (
    url.searchParams.get("shared") === roomId &&
    !url.searchParams.has("invite")
  ) {
    return;
  }
  url.searchParams.set("shared", roomId);
  url.searchParams.delete("invite");
  window.history.replaceState({}, "", url.toString());
}

/** Strip `?shared=` from the URL. No-op if it isn't present. */
export function clearSharedUrlParam(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("shared")) return;
  url.searchParams.delete("shared");
  window.history.replaceState({}, "", url.toString());
}

/**
 * Default reaction when the active shared room becomes inaccessible
 * mid-session (owner deleted, member removed, Firestore write denied).
 * Tears down the session, clears the URL, and surfaces an alert so the
 * user understands why their session ended.
 */
function defaultAccessLostHandler(reason: string): void {
  stopSharedRoomSync();
  useDesign.getState().clearActiveSharedRoom();
  clearSharedUrlParam();
  if (typeof window !== "undefined") {
    window.alert(reason);
  }
}
